"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const bignumber_1 = require("@ethersproject/bignumber");
const ethers_1 = require("ethers");
const lodash_1 = __importDefault(require("lodash"));
const base_1 = require("../base");
const constant_1 = require("../constant");
const util_1 = require("../util");
const axios_1 = __importDefault(require("axios"));
const conversionPath = {
    usdc: ['usdc', 'tonic'],
    usdt: ['usdt', 'wcro', 'tonic'],
    weth: ['weth', 'wcro', 'tonic'],
    wbtc: ['wbtc', 'wcro', 'tonic'],
    cro: ['cro', 'wcro', 'tonic'],
    dai: ['dai', 'wcro', 'tonic'],
};
const tokenAddress = {
    tonic: '0xDD73dEa10ABC2Bff99c60882EC5b2B81Bb1Dc5B2',
    usdc: '0xc21223249CA28397B4B6541dfFaEcC539BfF0c59',
    usdt: '0x66e428c3f67a68878562e79A0234c1F83c208770',
    weth: '0xe44Fd7fCb2b1581822D0c862B68222998a0c299a',
    wbtc: '0x062E66477Faf219F25D27dCED647BF57C3107d52',
    wcro: '0x5C7F8A570d578ED84E63fdFA7b1eE72dEae1AE23',
    cro: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
    dai: '0xF2001B145b43032AAF5Ee2884e456CCd805F677D',
};
class StakingModule extends base_1.TectonicSDKModule {
    async stake(walletAddress, amount) {
        const stakingPool = this.getTectonicStakingPool();
        const tonic = this.getContractByName('TONIC');
        if ((await tonic.allowance(walletAddress, stakingPool.address)).lt(bignumber_1.BigNumber.from(amount))) {
            await tonic
                .approve(stakingPool.address, ethers_1.ethers.constants.MaxUint256)
                .then((tx) => tx.wait());
        }
        return (0, util_1.wrapSdkTransaction)(stakingPool.stake(bignumber_1.BigNumber.from(amount)));
    }
    async unstake(walletAddress, amount) {
        const stakingPool = this.getTectonicStakingPool();
        const xTonic = this.getContractByName('XTonic');
        if ((await xTonic.allowance(walletAddress, stakingPool.address)).lt(bignumber_1.BigNumber.from(amount))) {
            await xTonic
                .approve(stakingPool.address, ethers_1.ethers.constants.MaxUint256)
                .then((tx) => tx.wait());
        }
        return (0, util_1.wrapSdkTransaction)(stakingPool.unstake(bignumber_1.BigNumber.from(amount)));
    }
    async release() {
        const stakingPool = this.getTectonicStakingPool();
        return (0, util_1.wrapSdkTransaction)(stakingPool.release());
    }
    async getExchangeRate() {
        const stakingPool = this.getTectonicStakingPool();
        const rawExRate = await stakingPool.getExchangeRate();
        // return 1/rawExRate but keeping scaled by 10^18
        // the exchangeRateBase is 1e20 (100 Ether)
        // 36 + 2 = 38
        return bignumber_1.BigNumber.from(10).pow(38).div(rawExRate);
    }
    async getLatestApy() {
        const apyResponse = (await axios_1.default.get(constant_1.TectonicConst.API_URL + 'info/staking/apy')).data;
        const { latestApy } = apyResponse;
        return ethers_1.ethers.utils.parseEther(latestApy.toString());
    }
    async getLatestApyFromChainEvents() {
        const stakingPool = this.getTectonicStakingPool();
        // const prevBlock = currentBlock - TectonicConst.BLOCKS_PER_DAY;
        // Get all events
        // const scanSize = 2000;
        const scanStartBlock = await this.provider.getBlockNumber();
        const scanStopBlock = scanStartBlock - constant_1.TectonicConst.BLOCKS_PER_DAY;
        // console.log(`scanStartBlock: ${scanStartBlock}`);
        // console.log(`scanStopBlock: ${scanStopBlock}`);
        let ptr = scanStartBlock;
        const events = [];
        while (ptr > scanStopBlock) {
            const nextPtr = Math.max(ptr - 2000, scanStopBlock);
            // console.log(`Scanning: ${ptr} - ${nextPtr}`);
            const batchEvent = await stakingPool.queryFilter(stakingPool.filters.ExchangeRateUpdated(null, null), nextPtr, ptr);
            events.push(...batchEvent);
            ptr = nextPtr;
        }
        if (events.length < 2) {
            return bignumber_1.BigNumber.from(0);
        }
        const sortedTuples = lodash_1.default.sortBy(events.map((event) => [
            event.args.newExchangeRate,
            event.args.timestamp.toNumber(),
        ]), (tuple) => tuple[1]);
        // First tuple is the oldest, last tuple is the newest
        const firstTuple = lodash_1.default.first(sortedTuples);
        const lastTuple = lodash_1.default.last(sortedTuples);
        const duration = lastTuple[1] - firstTuple[1];
        // console.log(sortedTuples);
        // console.log(duration);
        // if (duration < 0) {
        if (duration < constant_1.TectonicConst.APY_MIN_EVENT_DURATION) {
            return bignumber_1.BigNumber.from(0);
        }
        // console.log("Tuples: ", sortedTuples.map(p => `${ethers.utils.formatEther(p[0])} @ ${p[1]}`));
        // Older tuple should have higher exchange rate. this delta should be positive number
        const exchangeRateDelta = firstTuple[0].sub(lastTuple[0]);
        // console.log("Exchange rate delta: ", ethers.utils.formatEther(exchangeRateDelta));
        // console.log("Exchange rate delta duration: ", duration);
        // Calculate APY: Given X is the oldest exchange rate, and Y is the newest exchange rate,
        // APY = (x - y) / y / ((duration between X and Y) / seconds per year)
        // const tmpYielding = exchangeRateDelta.mul(ethers.constants.WeiPerEther).div(lastTuple[0])
        const APY = exchangeRateDelta
            .mul(ethers_1.ethers.constants.WeiPerEther)
            .mul(constant_1.TectonicConst.SECONDS_PER_YEAR)
            .div(lastTuple[0])
            .div(duration);
        return APY;
    }
    async performConversionForTokens(paths, amounts) {
        const stakingPool = this.getTectonicStakingPool();
        let bigNumberAmounts = amounts.map((a) => bignumber_1.BigNumber.from(a));
        return (0, util_1.wrapSdkTransaction)(stakingPool.performConversionForTokens(paths, bigNumberAmounts));
    }
    async performConversionForTokensFullBalance(paths) {
        const stakingPool = this.getTectonicStakingPool();
        return (0, util_1.wrapSdkTransaction)(stakingPool.performConversionForTokensFullBalance(paths));
    }
    //xTonic amount
    async getUnstakedToken(ownerAddress) {
        const stakingPool = this.getTectonicStakingPool();
        return await stakingPool.getCooldownTokens(ownerAddress);
    }
    //xTonic amount
    async getReleasableToken(ownerAddress) {
        const stakingPool = this.getTectonicStakingPool();
        return await stakingPool.getReleasableTokens(ownerAddress);
    }
    async getCountDownSeconds(ownerAddress) {
        const stakingPool = this.getTectonicStakingPool();
        const coolDownBlockNum = await stakingPool.getCooldownBlockNum(ownerAddress);
        if (coolDownBlockNum.toNumber() === 0) {
            return 0;
        }
        const currentBlockNum = await this.provider.getBlockNumber();
        return coolDownBlockNum
            .sub(currentBlockNum)
            .mul(constant_1.TectonicConst.SECONDS_PER_BLOCK)
            .toNumber();
    }
    async getTotalStakedAmount() {
        const stakingPool = this.getTectonicStakingPool();
        const tonic = super.getTonic();
        return tonic.balanceOf(stakingPool.address);
        // const xTonic = this.getXTonic();
        // // Get Minted Amount
        // const mintedAmount = await xTonic.totalSupply();
        // const rawExRate =  await stakingPool.getExchangeRate();
        // // mintedAmount / exchangeRate * (10^2) = totalStakedAmount
        // return mintedAmount.mul(100).div(rawExRate);
    }
    async getLockedExchangeRate(ownerAddress) {
        const stakingPool = this.getTectonicStakingPool();
        let lockedExRate = await stakingPool.withdrawalExchangeRates(ownerAddress);
        if (lockedExRate.eq(bignumber_1.BigNumber.from(0))) {
            lockedExRate = await stakingPool.getExchangeRate();
        }
        // return 1/rawExRate but keeping scaled by 10^18
        // the exchangeRateBase is 1e20 (100 Ether)
        // 36 + 2 = 38
        return bignumber_1.BigNumber.from(10).pow(38).div(lockedExRate);
    }
    async estimatePullAndConvertGasCost() {
        const spHelper = this.getContractByName('TectonicSPBountyHelper');
        const paths = Object.values(conversionPath).map((path) => path.map((n) => tokenAddress[n]));
        const gasCost = await spHelper.estimateGas.pullAndConvertFull(false, paths);
        return gasCost;
    }
    async estimatePullAndConvertYieldingAndReward() {
        const spHelper = this.getContractByName('TectonicSPBountyHelper');
        const paths = Object.values(conversionPath).map((path) => path.map((n) => tokenAddress[n]));
        const [yielding, reward] = await spHelper.callStatic.pendingYieldingAndRewards(paths);
        return [yielding, reward];
    }
    async pullAndConvert() {
        const spHelper = this.getContractByName('TectonicSPBountyHelper');
        const paths = Object.values(conversionPath).map((path) => path.map((n) => tokenAddress[n]));
        return (0, util_1.wrapSdkTransaction)(spHelper.pullAndConvertFull(false, paths));
    }
    getXTonic() {
        return super.getXTonic();
    }
}
exports.default = StakingModule;
//# sourceMappingURL=staking.js.map